/* ============================================================
   Concierge launcher click — wired up at script-execution time,
   BEFORE DOMContentLoaded. The script tag is `defer`, so the DOM
   is already built when this runs. This bypass guarantees the
   chat button responds to clicks regardless of any later init
   error inside DOMContentLoaded.
   ============================================================ */
(function () {
    document.addEventListener("click", function (e) {
        const launcher = e.target && e.target.closest && e.target.closest("#archerConciergeToggle");
        if (launcher) {
            e.preventDefault();
            const panel = document.getElementById("archerConciergePanel");
            if (!panel) return;
            panel.classList.add("is-open");
            panel.setAttribute("aria-hidden", "false");
            launcher.setAttribute("aria-expanded", "true");
            document.body.classList.add("archer-concierge-open");
            return;
        }
        const closer = e.target && e.target.closest && e.target.closest("[data-acp-close]");
        if (closer) {
            const panel = closer.closest("#archerConciergePanel") || document.getElementById("archerConciergePanel");
            if (!panel) return;
            e.preventDefault();
            panel.classList.remove("is-open");
            panel.setAttribute("aria-hidden", "true");
            const t = document.getElementById("archerConciergeToggle");
            if (t) t.setAttribute("aria-expanded", "false");
            document.body.classList.remove("archer-concierge-open");
            return;
        }
        const openPanel = document.getElementById("archerConciergePanel");
        if (!openPanel || !openPanel.classList.contains("is-open")) return;
        const toggleEl = document.getElementById("archerConciergeToggle");
        if (openPanel.contains(e.target) || (toggleEl && toggleEl.contains(e.target))) return;
        openPanel.classList.remove("is-open");
        openPanel.setAttribute("aria-hidden", "true");
        if (toggleEl) toggleEl.setAttribute("aria-expanded", "false");
        document.body.classList.remove("archer-concierge-open");
    });
    document.addEventListener("keydown", function (e) {
        if (e.key !== "Escape") return;
        const panel = document.getElementById("archerConciergePanel");
        if (!panel || !panel.classList.contains("is-open")) return;
        panel.classList.remove("is-open");
        panel.setAttribute("aria-hidden", "true");
        const t = document.getElementById("archerConciergeToggle");
        if (t) t.setAttribute("aria-expanded", "false");
        document.body.classList.remove("archer-concierge-open");
    });
})();

document.addEventListener("DOMContentLoaded", function () {

    /* Before/after sliders — drag horizontally to wipe between Day 1 and Day 30.
       Performance: rect is cached on pointerdown, position updates are coalesced
       to one per animation frame via requestAnimationFrame, and the underlying
       reveal is a wrapper width change (GPU-cheap) rather than clip-path. */
    function initBaSliders() {
        const sliders = document.querySelectorAll("[data-ba-slider]");
        if (!sliders.length) return;

        // Set the inner image's pixel width to match the slider so it stays
        // visually fixed while the wrapper's width animates. Re-run on resize.
        function setSliderWidth(slider) {
            const w = slider.getBoundingClientRect().width;
            slider.style.setProperty("--ba-slider-width", w + "px");
        }
        sliders.forEach(setSliderWidth);
        let resizeRaf = null;
        window.addEventListener("resize", function () {
            if (resizeRaf) return;
            resizeRaf = requestAnimationFrame(function () {
                resizeRaf = null;
                sliders.forEach(setSliderWidth);
            });
        });

        sliders.forEach(function (slider) {
            const handle = slider.querySelector(".ba-handle");
            if (!handle) return;

            slider.classList.add("swiper-no-swiping");

            let dragging = false;
            let cachedRect = null;
            let pendingX = 0;
            let rafId = null;

            function flush() {
                rafId = null;
                if (!cachedRect) return;
                const x = Math.max(0, Math.min(pendingX - cachedRect.left, cachedRect.width));
                const pct = (x / cachedRect.width) * 100;
                slider.style.setProperty("--ba-pos", pct + "%");
            }

            function schedule(clientX) {
                pendingX = clientX;
                if (rafId == null) rafId = requestAnimationFrame(flush);
            }

            function onPointerMove(e) {
                if (!dragging) return;
                const x = e.touches ? e.touches[0].clientX : e.clientX;
                schedule(x);
                if (e.cancelable) e.preventDefault();
            }

            function onPointerUp() {
                dragging = false;
                cachedRect = null;
                if (rafId != null) {
                    cancelAnimationFrame(rafId);
                    rafId = null;
                }
                document.removeEventListener("mousemove", onPointerMove);
                document.removeEventListener("mouseup", onPointerUp);
                document.removeEventListener("touchmove", onPointerMove);
                document.removeEventListener("touchend", onPointerUp);
            }

            function onPointerDown(e) {
                if (e.cancelable && !e.touches) e.preventDefault();
                e.stopPropagation();
                dragging = true;
                cachedRect = slider.getBoundingClientRect();
                setSliderWidth(slider);
                const x = e.touches ? e.touches[0].clientX : e.clientX;
                schedule(x);
                document.addEventListener("mousemove", onPointerMove);
                document.addEventListener("mouseup", onPointerUp);
                document.addEventListener("touchmove", onPointerMove, { passive: false });
                document.addEventListener("touchend", onPointerUp);
            }

            slider.addEventListener("mousedown", onPointerDown);
            slider.addEventListener("touchstart", onPointerDown, { passive: true });
            handle.addEventListener("keydown", function (e) {
                const cur = parseFloat(getComputedStyle(slider).getPropertyValue("--ba-pos")) || 50;
                if (e.key === "ArrowLeft")  slider.style.setProperty("--ba-pos", Math.max(0, cur - 10) + "%");
                if (e.key === "ArrowRight") slider.style.setProperty("--ba-pos", Math.min(100, cur + 10) + "%");
            });
        });
    }

    /* Full-screen menu drawer — opens from the left, ESC / overlay / X to close. */
    function initMenuDrawer() {
        const toggle = document.querySelector(".ah-menu-toggle");
        const drawer = document.getElementById("ahMenuDrawer");
        if (!toggle || !drawer) return;

        const closeEls = drawer.querySelectorAll("[data-ah-close]");
        const links = drawer.querySelectorAll(".ah-menu-primary a, .ah-menu-secondary a");

        function open() {
            drawer.classList.add("is-open");
            drawer.setAttribute("aria-hidden", "false");
        }
        function close() {
            drawer.classList.remove("is-open");
            drawer.setAttribute("aria-hidden", "true");
        }

        toggle.addEventListener("click", open);
        closeEls.forEach(el => el.addEventListener("click", close));
        links.forEach(el => el.addEventListener("click", close));
        document.addEventListener("keydown", function (e) {
            if (e.key === "Escape" && drawer.classList.contains("is-open")) close();
        });
    }

    /* Toggle the theme's transparent-header body class based on the
       active slide's data-header-color. The theme already styles
       transparent-header with white logo/icons/nav. */
    /* Sticky buy banner — hidden on slide 1 (the product slide),
       slides down on every other slide so users can checkout from anywhere. */
    function applyStickyBuy(swiperInstance) {
        const banner = document.getElementById("vshStickyBuy");
        if (!banner) return;
        const showOff1 = swiperInstance.activeIndex !== 0;
        banner.classList.toggle("is-visible", showOff1);
        banner.setAttribute("aria-hidden", showOff1 ? "false" : "true");
    }

    function applyHeaderColor(swiperInstance) {
        const activeSlide = swiperInstance.slides[swiperInstance.activeIndex];
        if (!activeSlide) return;
        const color = activeSlide.dataset.headerColor || "light";
        document.body.classList.toggle("transparent-header", color === "light");
    }

    /* Toggle a body class when the user is on the last slide. Originally
       this also disabled mousewheel + touch so the page could scroll into
       a non-swiper footer below — but the entire homepage now lives inside
       the swiper, so disabling input traps the user on the last slide.
       Keep the body class for any styling that targets it. */
    function applyLastSlideScroll(swiperInstance) {
        const isLast = swiperInstance.activeIndex === swiperInstance.slides.length - 1;
        document.body.classList.toggle("vsh-on-last-slide", isLast);
    }

    function applyHeroSlideClass(swiperInstance) {
        const isHero = swiperInstance.activeIndex === 0;
        document.body.classList.toggle("vsh-on-hero-slide", isHero);
    }

    /* Inline-paragraph slide reveal — when the slide becomes active,
       stagger-darken each word from #d8d8d8 → #111 with a small per-word
       delay so the line feels like it's being read into existence.
       When the slide goes inactive, reset so the animation re-runs next
       time the user lands on it. */
    function applyInlineParagraphReveal(swiperInstance) {
        swiperInstance.slides.forEach(function (slide, idx) {
            if (!slide.matches("[data-inline-paragraph]")) return;
            const isActive = idx === swiperInstance.activeIndex;
            if (!isActive) {
                slide.classList.remove("is-paragraph-active");
                return;
            }
            const words = slide.querySelectorAll(".vsh-inline-word");
            if (!words.length) return;
            // Spread the reveal across ~2.4s so each word reads as a deliberate
            // beat in the cascade, with a per-word floor of 100ms so the stagger
            // is visible even on short paragraphs.
            const totalMs = 2400;
            const perMs = Math.min(100, totalMs / words.length);
            words.forEach(function (w, i) {
                w.style.transitionDelay = (i * perMs) + "ms";
            });
            // Force a frame so the transition fires from the unstyled state.
            requestAnimationFrame(function () {
                slide.classList.add("is-paragraph-active");
            });
        });
    }

    function init() {
        if (typeof Swiper === "undefined") {
            // Swiper bundle is loaded with `defer`, so it may arrive after this listener fires.
            // Retry once it's on the window.
            return setTimeout(init, 50);
        }

        const wrappers = document.querySelectorAll(".vsh-wrapper");

        wrappers.forEach(function (wrapper) {

            const swiperEl = wrapper.querySelector(".vsh-swiper");
            const paginationEl = wrapper.querySelector(".vsh-pagination");
            const speed = parseInt(wrapper.dataset.vshSpeed, 10) || 300;

            /* Mirrors ysl.com's Swiper config exactly:
               creative effect where the next slide rises *over* the current one.
               Forward: current slide stays put (z=-1, behind), next translates
               up from y=100% to cover it. Backward: current slides down off
               screen, revealing prev that was sitting behind.
               Inspected from production YSL homepage on 2026-05-06. */
            wrapper._vshSwiper = new Swiper(swiperEl, {
                direction: "vertical",
                slidesPerView: 1,
                spaceBetween: 0,
                speed: speed,
                initialSlide: 0,
                effect: "creative",
                creativeEffect: {
                    limitProgress: 1,
                    shadowPerProgress: false,
                    progressMultiplier: 1,
                    perspective: true,
                    prev: {
                        translate: [0, 0, -1],
                        rotate: [0, 0, 0],
                        opacity: 1,
                        scale: 1,
                    },
                    next: {
                        translate: [0, "100%", 0],
                        rotate: [0, 0, 0],
                        opacity: 1,
                        scale: 1,
                    },
                },
                mousewheel: {
                    enabled: true,
                    thresholdDelta: 10,
                    thresholdTime: 300,
                    forceToAxis: true,
                    sensitivity: 1,
                },
                keyboard: {
                    enabled: true,
                    onlyInViewport: true,
                },
                pagination: paginationEl ? {
                    el: paginationEl,
                    clickable: true,
                } : false,
                on: {
                    init: function () {
                        wrapper.classList.add("vsh-ready");
                        applyHeaderColor(this);
                        applyLastSlideScroll(this);
                        applyStickyBuy(this);
                        applyHeroSlideClass(this);
                        applyInlineParagraphReveal(this);
                    },
                    slideChangeTransitionStart: function () {
                        applyHeaderColor(this);
                        applyLastSlideScroll(this);
                        applyStickyBuy(this);
                        applyHeroSlideClass(this);
                        applyInlineParagraphReveal(this);
                    },
                    slideChangeTransitionEnd: function () {
                        const slide = this.slides[this.activeIndex];
                        if (slide && slide.classList.contains("vsh-product-slide")) {
                            nudgeShopifyWallets(slide);
                        }
                    }
                }
            });
        });
    }

    /* Concierge shift logic — Sara covers US daytime (9am–6pm CT),
       Philippa covers after-hours. Anchored to US Central as a
       middle-of-the-country reference (UTC-6, CST, year-round). */
    function activeConcierge() {
        const nowUtcHour = new Date().getUTCHours();
        const ctHour = (nowUtcHour - 6 + 24) % 24;
        const isUsDaytime = ctHour >= 9 && ctHour < 18;
        return isUsDaytime ? "sara" : "philippa";
    }
    function applyConcierge(panel) {
        const who = activeConcierge();
        const isPhilippa = who === "philippa";
        const name = isPhilippa ? "Philippa" : "Sara";
        const imgUrl = isPhilippa ? panel.dataset.philippaImg : panel.dataset.saraImg;
        const initial = panel.querySelector("[data-acp-initial]");
        const img = panel.querySelector("[data-acp-image]");
        const greeting = panel.querySelector("[data-acp-greeting]");
        if (initial) initial.textContent = name[0];
        if (img) {
            img.src = imgUrl;
            img.alt = `${name}, ARCHER concierge team`;
            img.style.display = "";
        }
        if (greeting) greeting.textContent = `Welcome. I'm ${name}, part of the concierge team at ARCHER.`;
    }

    function initConcierge() {
        const toggle = document.getElementById("archerConciergeToggle");
        const panel = document.getElementById("archerConciergePanel");
        if (!toggle || !panel) return;

        applyConcierge(panel);

        function open() {
            applyConcierge(panel);
            panel.classList.add("is-open");
            panel.setAttribute("aria-hidden", "false");
            toggle.setAttribute("aria-expanded", "true");
            document.body.classList.add("archer-concierge-open");
        }
        function close() {
            panel.classList.remove("is-open");
            panel.setAttribute("aria-hidden", "true");
            toggle.setAttribute("aria-expanded", "false");
            document.body.classList.remove("archer-concierge-open");
        }

        /* Event delegation on document — survives DOM replacement, init-order
           bugs, and any earlier init function throwing. The launcher always
           opens regardless of when this binding runs. */
        document.addEventListener("click", function (e) {
            const launcher = e.target.closest("#archerConciergeToggle");
            if (launcher) {
                e.preventDefault();
                open();
                return;
            }
            const closer = e.target.closest("[data-acp-close]");
            if (closer && panel.contains(closer)) {
                e.preventDefault();
                close();
                return;
            }
            /* Outside-click close — only when panel is open and click is
               outside both panel and the launcher button. */
            if (!panel.classList.contains("is-open")) return;
            if (panel.contains(e.target) || toggle.contains(e.target)) return;
            close();
        });
        document.addEventListener("keydown", function (e) {
            if (e.key === "Escape" && panel.classList.contains("is-open")) close();
        });

        const form = panel.querySelector("[data-acp-form]");
        const intro = panel.querySelector(".acp-intro");
        const confirm = panel.querySelector("[data-acp-confirm]");
        if (form && confirm) {
            form.addEventListener("submit", function (e) {
                e.preventDefault();
                const name = (form.elements.name?.value || "").trim();
                const email = (form.elements.email?.value || "").trim();
                if (!name || !email) return;
                form.hidden = true;
                if (intro) intro.hidden = true;
                confirm.hidden = false;
                confirm.textContent = `Thank you, ${name.split(" ")[0]}. A member of the ARCHER concierge team will be in touch shortly at ${email}.`;
            });
        }
    }

    /* Mini-PDP gallery — clicking a thumbnail swaps the hero image src.
       No carousel, no fade — instant swap so it stays cheap.
       Arrow buttons step through real (non-placeholder) thumbs with wrap. */
    function initProductGallery() {
        document.querySelectorAll(".vsh-product-slide").forEach(function (slide) {
            const hero = slide.querySelector(".vsh-product-hero-img");
            const thumbs = slide.querySelectorAll(".vsh-product-thumb");
            if (!hero || !thumbs.length) return;
            thumbs.forEach(function (btn) {
                btn.addEventListener("click", function () {
                    const src = btn.dataset.thumbSrc;
                    if (!src) return;
                    hero.src = src;
                    hero.srcset = "";
                    thumbs.forEach(t => t.classList.remove("is-active"));
                    btn.classList.add("is-active");
                });
            });

            const realThumbs = slide.querySelectorAll(".vsh-product-thumb:not(.is-placeholder)");
            if (realThumbs.length < 2) return;
            function step(direction) {
                let activeIdx = -1;
                realThumbs.forEach(function (t, i) { if (t.classList.contains("is-active")) activeIdx = i; });
                if (activeIdx === -1) activeIdx = 0;
                const nextIdx = (activeIdx + direction + realThumbs.length) % realThumbs.length;
                realThumbs[nextIdx].click();
            }
            const prev = slide.querySelector(".vsh-product-arrow-prev");
            const next = slide.querySelector(".vsh-product-arrow-next");
            if (prev) prev.addEventListener("click", function () { step(-1); });
            if (next) next.addEventListener("click", function () { step(1); });
        });
    }

    /* Live viewer counter — small biased random walk so the number drifts
       naturally. Heavily weighted toward 0/+1 with rare +2; downward moves
       only ever -1 and only when we're not already near the floor. Result:
       a count that ebbs and flows but never visibly drops in a way that
       would trigger a "why are people leaving?" reaction. */
    function initViewerCounters() {
        document.querySelectorAll("[data-vsh-viewers]").forEach(function (el) {
            const target = el.querySelector("[data-vsh-viewers-count]");
            if (!target) return;
            const min = parseInt(el.dataset.min, 10) || 50;
            const max = parseInt(el.dataset.max, 10) || 80;
            let value = parseInt(el.dataset.start, 10) || parseInt(target.textContent, 10) || 64;
            value = Math.max(min, Math.min(max, value));
            target.textContent = value;

            // Bag of moves with strong upward/neutral bias. Big moves never appear.
            const moves = [-1, 0, 0, 0, 0, +1, +1, +1, +1, +2];

            function tick() {
                let m = moves[Math.floor(Math.random() * moves.length)];
                // Soft edge-handling: when close to bounds, lean inward.
                if (value >= max - 1 && m > 0) m = 0;
                if (value <= min + 1 && m < 0) m = 0;
                value = Math.max(min, Math.min(max, value + m));
                target.textContent = value;
                setTimeout(tick, 4000 + Math.random() * 6000); // 4–10s
            }
            // Stagger the first tick so it doesn't fire on slide enter.
            setTimeout(tick, 5000 + Math.random() * 4000);
        });
    }

    /* Re-mount Shopify's payment-button host element when the product slide
       becomes visible. The accelerated-checkout JS that renders Apple Pay /
       Google Pay / PayPal alongside Shop Pay decides which wallets to show
       based on the form's bounding box at first detection. Inside Swiper the
       form starts off-screen via `transform: translate3d(0, 100%, 0)`, so the
       initial detection picks Shop Pay only. Removing and re-inserting the
       host (or its `outerHTML`) forces Shopify's MutationObserver to re-run
       detection while the slide is on-screen, at which point all configured
       wallets render. We also nudge a resize event to cover the case where
       the JS is listening for layout changes rather than DOM mutations. */
    /* Force Shopify's portable-wallets module to (re-)initialize the
       in-slide payment_button. The bootstrap script in the page header
       only auto-runs init once on DOMContentLoaded; if the form lives in
       a Swiper slide that's transformed off-screen at that moment, the
       module skips full wallet enhancement. We poke init() repeatedly
       until the wallet host has more than just the shop-pay skeleton. */
    function initShopifyPaymentInit() {
        function poke() {
            if (window.Shopify && window.Shopify.PaymentButton && typeof window.Shopify.PaymentButton.init === "function") {
                try { window.Shopify.PaymentButton.init(); } catch (e) {}
            }
        }
        let attempts = 0;
        const maxAttempts = 20;
        const interval = setInterval(function () {
            attempts += 1;
            poke();
            const host = document.querySelector(".vsh-product-slide .shopify-payment-button");
            const populated = host && host.querySelectorAll(
                "button:not([disabled]):not(.shopify-payment-button__skeleton), iframe, shop-pay-wallet-button"
            ).length > 1;
            if (populated || attempts >= maxAttempts) clearInterval(interval);
        }, 600);
    }

    function nudgeShopifyWallets(_slide) { /* unused — kept as no-op for compat */ }

    /* Skip the cart page entirely — when the product slide's add-to-cart form
       is submitted, AJAX-add the variant and send the buyer straight to
       Shopify checkout. There is no native "skip cart" toggle in Shopify, so
       this is the standard pattern. */
    function initSkipCart() {
        document.querySelectorAll('.vsh-product-form').forEach((form) => {
            form.addEventListener('submit', (e) => {
                e.preventDefault();
                e.stopImmediatePropagation();
                const submitBtn = form.querySelector('button[type="submit"]');
                if (submitBtn) submitBtn.disabled = true;
                const data = new FormData(form);
                fetch('/cart/add.js', {
                    method: 'POST',
                    body: data,
                    headers: { 'Accept': 'application/json' }
                })
                .then((r) => r.ok ? r.json() : Promise.reject(r))
                .then(() => { window.location.href = '/checkout'; })
                .catch(() => {
                    if (submitBtn) submitBtn.disabled = false;
                });
            }, { capture: true });
        });
    }

    function initPaypathToggle() {
        document.querySelectorAll('.vsh-product-paypaths input[type="radio"]').forEach((radio) => {
            let wasChecked = false;
            radio.addEventListener('mousedown', () => { wasChecked = radio.checked; });
            radio.addEventListener('keydown', (e) => {
                if (e.key === ' ' || e.key === 'Enter') wasChecked = radio.checked;
            });
            radio.addEventListener('click', () => {
                if (wasChecked) {
                    radio.checked = false;
                    wasChecked = false;
                    radio.dispatchEvent(new Event('change', { bubbles: true }));
                }
            });
        });
    }

    initMenuDrawer();
    initBaSliders();
    initConcierge();
    initProductGallery();
    initViewerCounters();
    initPaypathToggle();
    initSkipCart();
    initShopifyPaymentInit();
    init();
});

// ============================================================
// Customer reviews pager — additive, scoped to .vsh-reviews-paged.
// Responsive per-page: re-buckets items and rebuilds the number list on
// viewport change so the count fits the available width.
//   >=1100px → 10/page   |   600–1099px → 6/page   |   <600px → 4/page
// ============================================================
(function () {
    function perPageForWidth(w) {
        if (w >= 1100) return 10;
        if (w >= 600) return 6;
        return 4;
    }

    function initReviewsPager() {
        document.querySelectorAll('[data-vsh-reviews-paged]').forEach((root) => {
            if (root.dataset.vshReviewsInit === '1') return;
            root.dataset.vshReviewsInit = '1';

            const items = Array.from(root.querySelectorAll('.vsh-reviews-item'));
            const pagerList = root.querySelector('.vsh-reviews-pager-list');
            const pager = root.querySelector('.vsh-reviews-pager');
            const prev = root.querySelector('[data-vsh-reviews-prev]');
            const next = root.querySelector('[data-vsh-reviews-next]');

            let perPage = perPageForWidth(window.innerWidth);
            let totalPages = Math.max(1, Math.ceil(items.length / perPage));
            let currentPage = 1;

            // Bucket items into pages based on current perPage.
            function rebucket() {
                items.forEach((el, idx) => {
                    const page = Math.floor(idx / perPage) + 1;
                    el.dataset.page = String(page);
                });
                totalPages = Math.max(1, Math.ceil(items.length / perPage));
                root.dataset.totalPages = String(totalPages);
                root.dataset.perPage = String(perPage);
            }

            // Rebuild the number-button list to match totalPages.
            function rebuildPagerList() {
                if (!pagerList) return;
                pagerList.innerHTML = '';
                for (let p = 1; p <= totalPages; p++) {
                    const li = document.createElement('li');
                    const btn = document.createElement('button');
                    btn.type = 'button';
                    btn.className = 'vsh-reviews-pager-num';
                    btn.dataset.vshReviewsPage = String(p);
                    btn.setAttribute('aria-label', 'Page ' + p);
                    btn.textContent = String(p);
                    btn.addEventListener('click', () => setPage(p));
                    li.appendChild(btn);
                    pagerList.appendChild(li);
                }
                if (pager) {
                    if (totalPages <= 1) pager.setAttribute('hidden', '');
                    else pager.removeAttribute('hidden');
                }
            }

            function setPage(n) {
                if (n < 1) n = 1;
                if (n > totalPages) n = totalPages;
                currentPage = n;
                items.forEach((el) => {
                    if (parseInt(el.dataset.page, 10) === n) el.removeAttribute('hidden');
                    else el.setAttribute('hidden', '');
                });
                if (pagerList) {
                    pagerList.querySelectorAll('[data-vsh-reviews-page]').forEach((btn) => {
                        const pg = parseInt(btn.dataset.vshReviewsPage, 10);
                        if (pg === n) {
                            btn.classList.add('is-active');
                            btn.setAttribute('aria-current', 'page');
                        } else {
                            btn.classList.remove('is-active');
                            btn.removeAttribute('aria-current');
                        }
                    });
                }
                if (prev) prev.toggleAttribute('disabled', n <= 1);
                if (next) next.toggleAttribute('disabled', n >= totalPages);
                root.dataset.currentPage = String(n);
            }

            if (prev) prev.addEventListener('click', () => setPage(currentPage - 1));
            if (next) next.addEventListener('click', () => setPage(currentPage + 1));

            // Initial render.
            rebucket();
            rebuildPagerList();
            setPage(1);

            // Re-bucket + rebuild when the viewport crosses a breakpoint.
            let resizeRaf = null;
            window.addEventListener('resize', () => {
                if (resizeRaf) cancelAnimationFrame(resizeRaf);
                resizeRaf = requestAnimationFrame(() => {
                    const nextPerPage = perPageForWidth(window.innerWidth);
                    if (nextPerPage === perPage) return;
                    perPage = nextPerPage;
                    rebucket();
                    rebuildPagerList();
                    setPage(1);
                });
            }, { passive: true });
        });
    }

    if (document.readyState === 'loading') {
        document.addEventListener('DOMContentLoaded', initReviewsPager);
    } else {
        initReviewsPager();
    }
    document.addEventListener('shopify:section:load', initReviewsPager);
})();

// ============================================================
// Reviews theme filter — clicking a chip filters items, click again clears
// ============================================================
(function () {
    function initReviewsThemeFilter() {
        document.querySelectorAll('.vsh-reviews-layout').forEach((layout) => {
            if (layout.dataset.vshThemeFilterInit === '1') return;
            layout.dataset.vshThemeFilterInit = '1';

            const chips = layout.querySelectorAll('[data-vsh-reviews-theme]');
            const paged = layout.querySelector('[data-vsh-reviews-paged]');
            if (!chips.length || !paged) return;

            const items = paged.querySelectorAll('.vsh-reviews-item');
            const pager = paged.querySelector('.vsh-reviews-pager');
            const numBtns = paged.querySelectorAll('[data-vsh-reviews-page]');
            const prev = paged.querySelector('[data-vsh-reviews-prev]');
            const next = paged.querySelector('[data-vsh-reviews-next]');
            const totalPages = parseInt(paged.dataset.totalPages, 10) || 1;

            let activeTheme = null;

            function clearFilter() {
                activeTheme = null;
                chips.forEach((c) => {
                    c.classList.remove('is-active');
                    c.setAttribute('aria-pressed', 'false');
                });
                if (pager) pager.removeAttribute('hidden');
                items.forEach((el) => {
                    if (parseInt(el.dataset.page, 10) === 1) el.removeAttribute('hidden');
                    else el.setAttribute('hidden', '');
                });
                numBtns.forEach((btn) => {
                    const pg = parseInt(btn.dataset.vshReviewsPage, 10);
                    btn.classList.toggle('is-active', pg === 1);
                    if (pg === 1) btn.setAttribute('aria-current', 'page');
                    else btn.removeAttribute('aria-current');
                });
                if (prev) prev.toggleAttribute('disabled', true);
                if (next) next.toggleAttribute('disabled', totalPages <= 1);
                paged.dataset.currentPage = '1';
            }

            function applyFilter(theme) {
                activeTheme = theme;
                chips.forEach((c) => {
                    const on = c.dataset.vshReviewsTheme === theme;
                    c.classList.toggle('is-active', on);
                    c.setAttribute('aria-pressed', on ? 'true' : 'false');
                });
                if (pager) pager.setAttribute('hidden', '');
                const needle = theme.toLowerCase();
                items.forEach((el) => {
                    const themes = (el.dataset.themes || '').split(',').map((s) => s.trim().toLowerCase()).filter(Boolean);
                    if (themes.includes(needle)) el.removeAttribute('hidden');
                    else el.setAttribute('hidden', '');
                });
            }

            chips.forEach((chip) => {
                chip.addEventListener('click', () => {
                    const t = chip.dataset.vshReviewsTheme;
                    if (activeTheme === t) clearFilter();
                    else applyFilter(t);
                });
            });
        });
    }

    if (document.readyState === 'loading') {
        document.addEventListener('DOMContentLoaded', initReviewsThemeFilter);
    } else {
        initReviewsThemeFilter();
    }
    document.addEventListener('shopify:section:load', initReviewsThemeFilter);
})();
